import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { getTradeBoot, receivedValue, valueTradeNow } from '../components/helpers';
import { buildTrades } from '../components/tradeLineage';
import {
  Page, Hero, StatStrip, ControlRail, Segmented, Empty, Thumb, Tag,
  useReveal, MoreButton,
  findSkinImage, money, signed, compactMoney, dateFull,
} from './mobileShared';

// Swaps — trades as vertical flow cards. The desktop draws gave → received
// left to right; a phone has no horizontal room for two item lists side by
// side, so the same flow runs top to bottom with the hub as a divider.

const FILTERS = [
  { key: 'all',      label: 'All' },
  { key: 'open',     label: 'Open' },
  { key: 'complete', label: 'Complete' },
];

export default function MobileTraded({ rows, settings, skinsData = [], livePrices }) {
  const [query, setQuery]   = useState('');
  const [filter, setFilter] = useState('all');

  const profC = 'var(--mb-pos)';
  const lossC = 'var(--mb-neg)';
  const pcol  = v => (v >= 0 ? profC : lossC);

  // Grouped by trade, not by item given: one Steam trade can hand over several
  // items sharing a tradeId. See buildTrades in components/tradeLineage.js.
  const trades = useMemo(() => {
    return buildTrades(rows).map((t, idx) => {
      const gaveRows = t.gave.map(e => e.row);
      const received = t.received.map(e => e.row);
      // See valueTradeNow in helpers.js: the basket is valued once, and no
      // per-item profit is invented out of the inherited cost slices.
      const worth = valueTradeNow(gaveRows, received, livePrices);
      return {
        key: t.tradeId || `legacy_${idx}`,
        offeredRow: gaveRows[0],
        gave: t.gave, at: t.at,
        received, worth,
        cash: getTradeBoot(gaveRows[0]),
        soldCount: worth.sold,
        pnl: worth.potential,
        complete: worth.settled,
      };
    });
  }, [rows, livePrices]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return trades.filter(t => {
      if (filter === 'open' && t.complete) return false;
      if (filter === 'complete' && !t.complete) return false;
      if (q) {
        const inOffered = t.gave.some(g => g.row.skin?.value?.toLowerCase().includes(q));
        const inRecv = t.received.some(r => r.skin?.value?.toLowerCase().includes(q));
        if (!inOffered && !inRecv) return false;
      }
      return true;
    });
  }, [trades, query, filter]);

  const totals = useMemo(() => ({
    pnl:   trades.reduce((s, t) => s + t.pnl, 0),
    cash:  trades.reduce((s, t) => s + t.cash, 0),
    open:  trades.filter(t => !t.complete).length,
    items: trades.reduce((s, t) => s + t.received.length, 0),
  }), [trades]);

  const [shown, hasMore, showMore] = useReveal(filtered, 6);

  if (!trades.length) {
    return (
      <Page>
        <Empty>No trades yet. Items you mark as traded show up here as swaps.</Empty>
      </Page>
    );
  }

  return (
    <Page>
      <Hero
        label="Total trade P/L"
        value={compactMoney(totals.pnl)}
        tone={totals.pnl >= 0 ? 'up' : 'down'}
        sub="real profit on settled trades, today’s market on the rest"
        foot={`${trades.length} trade${trades.length !== 1 ? 's' : ''} · ${totals.items} item${totals.items !== 1 ? 's' : ''} received${totals.open ? ` · ${totals.open} open` : ''}`}
      />

      <StatStrip items={[
        { label: 'Items received', value: String(totals.items), sub: `from ${trades.length} trade${trades.length !== 1 ? 's' : ''}` },
        { label: 'Still open', value: String(totals.open), sub: 'not fully sold on' },
        { label: 'Cash on the side', value: money(totals.cash), sub: 'sweetener received' },
      ]} />

      <ControlRail query={query} onQuery={setQuery} placeholder="Search trades…" />
      <Segmented options={FILTERS} value={filter} onChange={setFilter} />

      {filtered.length === 0 ? (
        <Empty>No trades match your filters.</Empty>
      ) : (
        <>
          {shown.map((t, ti) => {
            return (
              <motion.article
                key={t.key}
                className="mb-trade"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1], delay: Math.min(ti, 6) * 0.04 }}
              >
                <header className="mb-trade-head">
                  <span className="d">{dateFull(t.at)}</span>
                  <Tag tone={t.complete ? 'good' : 'warn'}>
                    {t.complete ? 'Complete' : t.received.length === 0 ? 'Nothing logged' : `${t.soldCount}/${t.received.length} sold`}
                  </Tag>
                </header>

                <div className="mb-trade-leg">
                  <span className="mb-trade-label">
                    Gave away{t.gave.length > 1 ? ` (${t.gave.length})` : ''}
                  </span>
                  {/* A list: drawing only the first given item is what made one
                      seven-item trade look like six that received nothing. */}
                  {t.gave.map(g => (
                    <div className="mb-trade-item" key={g.index}>
                      <Thumb src={findSkinImage(skinsData, g.row.skin?.value, g.row.condition?.value)} />
                      <div className="mb-row-main">
                        <div className="mb-row-name">{g.row.skin?.value}</div>
                        <div className="mb-row-meta">
                          <span>{g.row.condition?.value || 'n/a'}</span>
                          <span>cost {money(g.row.buyPrice)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mb-trade-hub">
                  <span className="arrow">↓</span>
                  {t.cash > 0 && <span className="cash">+{money(t.cash)} cash</span>}
                </div>

                <div className="mb-trade-leg">
                  <span className="mb-trade-label">Received ({t.received.length})</span>
                  {t.received.length === 0 ? (
                    <p className="mb-trade-none">No received items recorded for this trade.</p>
                  ) : t.received.map((r, i) => {
                    const img  = findSkinImage(skinsData, r.skin?.value, r.condition?.value);
                    const v    = receivedValue(r, livePrices);
                    const sold = v.sold;
                    return (
                      <div key={i} className={`mb-trade-item ${sold ? 'is-sold' : ''}`}>
                        <Thumb src={img} />
                        <div className="mb-row-main">
                          <div className="mb-row-name">{r.skin?.value}</div>
                          <div className="mb-row-meta">
                            <span>{r.condition?.value || 'n/a'}</span>
                            <span>{sold ? 'sold' : 'holding'}</span>
                          </div>
                        </div>
                        {/* Price, not profit — the cost slice each of these
                            inherited is an accounting split, not a purchase. */}
                        <span className="mb-trade-pnl">
                          {v.value != null ? `$${v.value.toFixed(2)}` : 'n/a'}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <footer className="mb-trade-foot">
                  <div><i>Gave away</i><b>${t.worth.gave.toFixed(2)}</b></div>
                  <div>
                    <i>{t.worth.settled ? 'Came back' : 'Worth today'}</i>
                    <b>${t.worth.back.toFixed(2)}</b>
                  </div>
                  <div>
                    <i>{t.worth.settled ? 'Profit' : 'Potential profit'}</i>
                    <b style={{ color: pcol(t.worth.potential) }}>{signed(t.worth.potential)}</b>
                  </div>
                </footer>
              </motion.article>
            );
          })}
          {hasMore && <MoreButton onClick={showMore} remaining={filtered.length - shown.length} />}
        </>
      )}
    </Page>
  );
}
